import React from "react";
import Text, { TextVariant } from "@/components/ui/text";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import SingleCheckbox from "@/components/ui/SingleCheckbox";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  confirmationText: string | JSX.Element;
  confirmationTextVariant?: TextVariant;
  handleSubmit: () => void;
  handleCancel?: () => void;
  isPrimaryButton?: boolean;
  submitButtonText?: string;
  cancelButtonText?: string;
  hideDivider?: boolean;
  checkboxLabel?: string;
  isCheckboxRequired?: boolean;
  onCheckboxChange?: (checked: boolean) => void;
  isCheckboxChecked?: boolean;
  headerClassname?: string;
  "data-test"?: string;
}

const ConfirmationModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  title,
  confirmationText,
  confirmationTextVariant = "bodySecondary",
  handleSubmit,
  handleCancel,
  isPrimaryButton = false,
  submitButtonText,
  cancelButtonText,
  hideDivider = false,
  checkboxLabel,
  isCheckboxRequired = false,
  onCheckboxChange,
  isCheckboxChecked = false,
  headerClassname,
  "data-test": dataTest
}) => {
  const { t } = useTranslation();

  const isSubmitDisabled = isCheckboxRequired && !isCheckboxChecked;

  const testId = dataTest || "confirmation-modal";

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      data-test={testId}
      contentClassname="w-[35rem] [&_[data-test='close-modal']]:w-8 [&_[data-test='close-modal']]:h-8 [&_[data-test='close-modal']]:scale-100"
      title={
        <Text as="div" variant="lgSubtitleH1" data-test={`${testId}-title`}>
          {title}
        </Text>
      }
      headerClassname={headerClassname}
      content={
        <div
          className="flex flex-col gap-3 h-fit w-[35rem] p-4"
          data-test={`${testId}-content`}
        >
          <Text as="div" variant={confirmationTextVariant}>
            {confirmationText}
          </Text>
          {checkboxLabel && onCheckboxChange && (
            <div className="mt-4">
              <SingleCheckbox
                id="confirmation-checkbox"
                label={checkboxLabel}
                isChecked={isCheckboxChecked}
                onCheckedChange={onCheckboxChange}
                labelVariant="body"
                data-test={`${testId}-checkbox`}
              />
            </div>
          )}
        </div>
      }
      footer={
        <div
          className="flex justify-end gap-2"
          data-test={`${testId}-footer`}
        >
          <Button
            data-test={`${testId}-cancel-button`}
            variant="secondary"
            onClick={handleCancel ? handleCancel : () => setIsOpen(false)}
          >
            {cancelButtonText || t("cancel")}
          </Button>
          <Button
            data-test={`${testId}-submit-button`}
            destructive={!isPrimaryButton}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            {submitButtonText ? submitButtonText : t("delete")}
          </Button>
        </div>
      }
      hideDivider={hideDivider}
    />
  );
};

export default ConfirmationModal;
